import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/Navbar'
import { Footer } from '../components/Footer'
import { Button } from '../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../components/ui/Card'
import { Select } from '../components/ui/Select'
import {Droplet, MapPin, Clock, Share2, Plus, Filter, CheckCircle, Eye, Calendar} from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { generateWhatsAppMessage, generateFacebookMessage, generateTwitterMessage, generateLinkedInMessage, generateInstagramMessage } from '../utils/shareTemplates'
import { logAudit } from '../utils/auditLogger'
import { analytics } from '../utils/analytics'
import { BRAZILIAN_CITIES, BRAZILIAN_STATES, getCitiesByState } from '../data/ibgeCities'

const bloodTypes = ['Todos', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-']
const urgencyLevels = ['Todos', 'critical', 'high', 'medium']

export function Requests() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [requests, setRequests] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filterBloodType, setFilterBloodType] = useState('Todos')
  const [filterUrgency, setFilterUrgency] = useState('Todos')
  const [filterState, setFilterState] = useState('Todos')
  const [filterCity, setFilterCity] = useState('Todos')
  const [sortBy, setSortBy] = useState('createdAt')
  const [userProfile, setUserProfile] = useState<any>(null)
  const [availableCities, setAvailableCities] = useState<string[]>([])
  const [showingLocalRequests, setShowingLocalRequests] = useState(true)

  useEffect(() => {
    let mounted = true;
    if (user && mounted) {
      loadUserProfile()
    }
    return () => { mounted = false; };
  }, [user])

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRequests()
    }, 500) // Debounce 500ms para melhor performance
    return () => clearTimeout(timer)
  }, [filterBloodType, filterUrgency, filterState, filterCity, sortBy])

  useEffect(() => {
    if (filterState !== 'Todos') {
      const cities = getCitiesByState(filterState)
      setAvailableCities(['Todos', ...cities.map(c => c.name)])
      setFilterCity('Todos')
    } else {
      setAvailableCities([])
    }
  }, [filterState])

  const loadUserProfile = async () => {
    if (!user) return
    try {
      const { list } = await lumi.entities.profiles.list({
        filter: { userId: user.userId }
      })
      if (list.length > 0) {
        const profile = list[0]
        setUserProfile(profile)
        
        // Auto-set filters to user's location on first load
        if (showingLocalRequests && profile.city && profile.state) {
          setFilterState(profile.state)
          setFilterCity(profile.city)
        }
      }
    } catch (error) {
      console.error('Failed to load profile:', error)
    }
  }

  const fetchRequests = async () => {
    setLoading(true)
    try {
      const filter: any = { status: 'active' }
      
      if (filterBloodType !== 'Todos') {
        filter.bloodType = filterBloodType
      }
      
      if (filterUrgency !== 'Todos') {
        filter.urgency = filterUrgency
      }
      
      if (filterState !== 'Todos') {
        filter.state = filterState
      }
      
      if (filterCity !== 'Todos') {
        filter.city = filterCity
      }

      const sortOptions: any = {}
      if (sortBy === 'expiresAt') {
        sortOptions.expiresAt = 1
      } else if (sortBy === 'urgency') {
        sortOptions.urgency = 1
        sortOptions.createdAt = -1
      } else {
        sortOptions.createdAt = -1
      }

      const { list } = await lumi.entities.requests.list({
        filter,
        sort: sortOptions,
        limit: 12 // Otimizado: carrega menos dados inicialmente
      })
      setRequests(list)
    } catch (error) {
      console.error('Failed to fetch requests:', error)
      toast.error('Erro ao carregar solicitações')
    } finally {
      setLoading(false)
    }
  }

  const handleShare = (req: any, platform?: 'whatsapp' | 'facebook' | 'twitter' | 'linkedin' | 'instagram') => {
    const shareData = {
      bloodType: req.bloodType,
      patientName: req.patientName,
      hospital: req.hospital,
      city: req.city,
      state: req.state,
      urgency: req.urgency,
      shareToken: req.shareToken || req._id
    }
    
    if (platform === 'whatsapp') {
      const msg = generateWhatsAppMessage(shareData)
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank')
      analytics.trackShare('whatsapp', req.bloodType, req.urgency)
      toast.success('📱 Abrindo WhatsApp...')
      return
    }
    
    if (platform === 'facebook') {
      const msg = generateFacebookMessage(shareData)
      navigator.clipboard.writeText(msg)
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin + '/solicitacao/' + shareData.shareToken)}`, '_blank')
      analytics.trackShare('facebook', req.bloodType, req.urgency)
      toast.success('📋 Mensagem copiada! Abrindo Facebook...')
      return
    }
    
    if (platform === 'twitter') {
      const msg = generateTwitterMessage(shareData)
      window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(msg)}`, '_blank')
      toast.success('🐦 Abrindo Twitter...')
      return
    }
    
    if (platform === 'linkedin') {
      const msg = generateLinkedInMessage(shareData)
      navigator.clipboard.writeText(msg)
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin + '/solicitacao/' + shareData.shareToken)}`, '_blank')
      toast.success('💼 Mensagem copiada! Abrindo LinkedIn...')
      return
    }
    
    if (platform === 'instagram') {
      const msg = generateInstagramMessage(shareData)
      navigator.clipboard.writeText(msg)
      toast.success('📸 Mensagem copiada! Cole no Instagram Stories ou post.')
      return
    }
    
    // Default: show all options
    const whatsappMsg = generateWhatsAppMessage(shareData)
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(whatsappMsg)}`
    
    if (navigator.share) {
      navigator.share({
        title: `Doação ${req.bloodType} - ${req.city}/${req.state}`,
        text: whatsappMsg
      }).catch(() => {
        window.open(whatsappUrl, '_blank')
      })
    } else {
      navigator.clipboard.writeText(whatsappMsg)
      toast.success('Mensagem copiada! Abrindo WhatsApp...')
      window.open(whatsappUrl, '_blank')
    }
  }

  const getUrgencyLabel = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'Crítico'
      case 'high': return 'Urgente'
      case 'medium': return 'Normal'
      default: return urgency
    }
  }

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'medium': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const handleConfirmDonation = async (req: any) => {
    if (!user || !userProfile) {
      toast.error('Faça login e complete seu perfil primeiro')
      return
    }

    try {
      // Update profile with new donation
      const newTotalDonations = (userProfile.totalDonations || 0) + 1
      const newPoints = (userProfile.points || 0) + 100
      const badges = userProfile.badges || []
      
      // Award badges
      if (newTotalDonations === 1 && !badges.includes('first_donation')) {
        badges.push('first_donation')
      }
      if (newTotalDonations >= 3 && !badges.includes('bronze_donor')) {
        badges.push('bronze_donor')
      }
      if (newTotalDonations >= 5 && !badges.includes('silver_donor')) {
        badges.push('silver_donor')
      }
      if (newTotalDonations >= 10 && !badges.includes('gold_donor')) {
        badges.push('gold_donor')
      }
      if (newTotalDonations >= 20 && !badges.includes('super_hero')) {
        badges.push('super_hero')
      }

      await lumi.entities.profiles.update(userProfile._id, {
        totalDonations: newTotalDonations,
        points: newPoints,
        badges: badges,
        lastDonationDate: new Date().toISOString()
      })

      // Track donation confirmation
      analytics.trackDonationConfirmation(req.bloodType, 100, newTotalDonations)

      // Log donation confirmation
      await logAudit({
        action: 'donation_confirmed',
        userId: user.userId,
        details: {
          requestId: req._id,
          bloodType: req.bloodType,
          pointsEarned: 100,
          totalDonations: newTotalDonations,
          newBadges: badges.filter((b: string) => !(userProfile.badges || []).includes(b))
        }
      })

      const newBadges = badges.filter((b: string) => !(userProfile.badges || []).includes(b))
      
      toast.success('🎉 Doação confirmada! +100 pontos conquistados', {
        duration: 4000,
        icon: '❤️'
      })
      
      if (newBadges.length > 0) {
        setTimeout(() => {
          toast.success(`🏆 Nova medalha desbloqueada: ${newBadges.map((b: string) => {
            const names: Record<string, string> = {
              first_donation: 'Primeira Doação',
              bronze_donor: 'Herói Bronze',
              silver_donor: 'Herói Prata',
              gold_donor: 'Herói Ouro',
              super_hero: 'Super Herói'
            }
            return names[b] || b
          }).join(', ')}!`, { duration: 5000 })
        }, 1000)
      }

      loadUserProfile()
      fetchRequests()
    } catch (error) {
      console.error('Failed to confirm donation:', error)
      toast.error('Erro ao confirmar doação')
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="container mx-auto px-4 py-6 pb-20">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Quem Precisa de Você Agora</h1>
            <p className="text-gray-600 mt-1">
              {userProfile && showingLocalRequests && filterCity !== 'Todos' ? (
                <>Solicitações em <strong>{filterCity}/{filterState}</strong> • {requests.length} {requests.length === 1 ? 'pessoa precisa' : 'pessoas precisam'} de você</>
              ) : (
                <>Cada pessoa aqui é uma história real. Você pode ser a diferença.</>
              )}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/preciso-doar')}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Solicitação
          </Button>
        </div>

        {/* Filters */}
        <Card className="mb-6 border border-red-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Filtrar por:</span>
              </div>
              {userProfile && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">Exibindo:</span>
                  <button
                    onClick={() => {
                      if (showingLocalRequests) {
                        setFilterState('Todos')
                        setFilterCity('Todos')
                        setShowingLocalRequests(false)
                      } else {
                        setFilterState(userProfile.state)
                        setFilterCity(userProfile.city)
                        setShowingLocalRequests(true)
                      }
                    }}
                    className="text-xs font-medium text-red-600 hover:text-red-700 underline"
                  >
                    {showingLocalRequests ? 'Ver todos os estados' : `Voltar para ${userProfile.city}`}
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label htmlFor="filterBloodType" className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo Sanguíneo
                </label>
                <Select
                  id="filterBloodType"
                  value={filterBloodType}
                  onChange={(e) => setFilterBloodType(e.target.value)}
                  aria-label="Filtrar por tipo sanguíneo"
                >
                  {bloodTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Urgência
                </label>
                <Select
                  value={filterUrgency}
                  onChange={(e) => setFilterUrgency(e.target.value)}
                >
                  {urgencyLevels.map(level => (
                    <option key={level} value={level}>
                      {level === 'Todos' ? 'Todos' : getUrgencyLabel(level)}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <Select
                  value={filterState}
                  onChange={(e) => {
                    setFilterState(e.target.value)
                    setShowingLocalRequests(false)
                  }}
                >
                  <option value="Todos">Todos</option>
                  {BRAZILIAN_STATES.map(state => (
                    <option key={state.code} value={state.code}>{state.code} - {state.name}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Município
                </label>
                <Select
                  value={filterCity}
                  onChange={(e) => {
                    setFilterCity(e.target.value)
                    setShowingLocalRequests(false)
                  }}
                  disabled={filterState === 'Todos'}
                >
                  {filterState === 'Todos' ? (
                    <option value="Todos">Selecione um estado primeiro</option>
                  ) : availableCities.length > 0 ? (
                    availableCities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))
                  ) : (
                    <option value="Todos">Todos</option>
                  )}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordenar por
                </label>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="createdAt">Mais Recentes</option>
                  <option value="expiresAt">Expira Primeiro</option>
                  <option value="urgency">Mais Urgentes</option>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Requests Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            [1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="h-80 border border-gray-200">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="w-14 h-14 rounded-full bg-gray-200 animate-pulse" />
                    <div className="w-16 h-6 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="mt-4 h-6 bg-gray-200 rounded animate-pulse w-3/4" />
                  <div className="space-y-2 mt-2">
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 bg-gray-200 rounded animate-pulse w-2/3" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-16 bg-gray-200 rounded animate-pulse mb-3" />
                  <div className="h-4 bg-gray-200 rounded animate-pulse" />
                </CardContent>
                <CardFooter className="gap-2">
                  <div className="flex-1 h-10 bg-gray-200 rounded animate-pulse" />
                  <div className="flex-1 h-10 bg-gray-200 rounded animate-pulse" />
                </CardFooter>
              </Card>
            ))
          ) : requests.length > 0 ? (
            requests.map((req) => (
              <Card key={req._id} className="hover:shadow-lg transition-shadow border-l-4 border-l-red-500">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100 text-red-700 font-bold text-xl" role="img" aria-label={`Tipo sanguíneo ${req.bloodType}`}>
                      {req.bloodType}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium uppercase ${getUrgencyColor(req.urgency)}`}>
                      {getUrgencyLabel(req.urgency)}
                    </span>
                  </div>
                  <CardTitle className="mt-4 text-lg">{req.patientName}</CardTitle>
                  <div className="space-y-1 mt-2">
                    <p className="text-sm text-gray-600 flex items-center">
                      <MapPin className="h-3 w-3 mr-1" /> {req.hospital}
                    </p>
                    <p className="text-sm text-gray-500">
                      {req.city}/{req.state}
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  {req.description && (
                    <p className="text-gray-600 text-sm line-clamp-3 mb-3">
                      {req.description}
                    </p>
                  )}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {new Date(req.createdAt).toLocaleDateString('pt-BR')}
                      </div>
                      <div className="text-red-600 font-medium">
                        {req.unitsNeeded} {req.unitsNeeded === 1 ? 'bolsa' : 'bolsas'}
                      </div>
                    </div>
                    {req.expiresAt && (() => {
                      const daysLeft = Math.ceil((new Date(req.expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                      return daysLeft > 0 ? (
                        <div className={`flex items-center text-xs font-medium ${
                          daysLeft <= 3 ? 'text-red-600' : daysLeft <= 7 ? 'text-orange-600' : 'text-blue-600'
                        }`}>
                          <Calendar className="h-3 w-3 mr-1" />
                          Expira em {daysLeft} {daysLeft === 1 ? 'dia' : 'dias'}
                        </div>
                      ) : (
                        <div className="flex items-center text-xs font-medium text-gray-500">
                          <Calendar className="h-3 w-3 mr-1" />
                          Expirado
                        </div>
                      )
                    })()}
                    {(req.viewCount || req.shareCount) && (
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span className="flex items-center">
                          <Eye className="h-3 w-3 mr-1" />
                          {req.viewCount || 0}
                        </span>
                        <span className="flex items-center">
                          <Share2 className="h-3 w-3 mr-1" />
                          {req.shareCount || 0}
                        </span>
                      </div>
                    )}
                  </div>
                  {req.contactInfo && (
                    <div className="mt-3 text-sm bg-gray-50 p-2 rounded">
                      <span className="font-medium">Contato:</span> {req.contactInfo}
                    </div>
                  )}
                </CardContent>
                <CardFooter className="gap-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => navigate(`/solicitacao/${req._id}`)}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Ver Detalhes
                  </Button>
                  {userProfile && userProfile.bloodType === req.bloodType ? (
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => handleConfirmDonation(req)}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Confirmo que Doei
                    </Button>
                  ) : (
                    <Button className="flex-1" disabled>
                      Tipo Incompatível
                    </Button>
                  )}
                </CardFooter>
              </Card>
            ))
          ) : filterBloodType !== 'Todos' || filterUrgency !== 'Todos' ? (
            <div className="col-span-3 text-center py-20">
              <Filter className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Nenhum pedido encontrado com esses filtros
              </h3>
              <p className="text-gray-600 mb-6">
                Tente ajustar os filtros ou ver todos os pedidos disponíveis.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setFilterBloodType('Todos')
                    setFilterUrgency('Todos')
                    setFilterState('Todos')
                    setFilterCity('Todos')
                    setShowingLocalRequests(false)
                  }}
                >
                  Limpar Filtros
                </Button>
                <Button onClick={() => navigate('/preciso-doar')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Criar Solicitação
                </Button>
              </div>
            </div>
          ) : (
            <div className="col-span-3 text-center py-20">
              <Droplet className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                🩸 Nenhuma solicitação urgente no momento
              </h3>
              <p className="text-gray-600 mb-6">
                Isso é um ótimo sinal! Mas você pode criar um pedido se precisar.
              </p>
              <Button onClick={() => navigate('/preciso-doar')}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Solicitação
              </Button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default Requests
